import { Box, Card, type Colors } from '@factorialco/deprecated-design-system'

import type { Filter } from 'modules/core/components/DataTable/types'
import i18n from 'modules/core/lib/i18n'

import { ChartSkeleton } from '../ChartSkeleton'
import { LoadingState } from './components/LoadingState'
import { PulseChart } from './components/PulseChart'
import { useEmployeePulseData } from './hooks/useEmployeePulseData'
import { getSelectedFilterAndOptionByIndex } from './lib/filterUtils'
import { COLORS, type EmployeePulseInsightsProps } from './types'

const hasInProperty = (obj: unknown): obj is { in?: unknown[] } => {
  return Boolean(obj && typeof obj === 'object' && 'in' in obj)
}

const getColorsBasedOnFilters = (
  dataTableFilters: Record<string, unknown>,
  enabledFilters: Filter<unknown>[],
  availableFilters: Filter<unknown>[]
): Colors[] => {
  return COLORS.map((color, index) => {
    const pulseAnswerFilter = dataTableFilters.pulseAnswer
    const selectedPulseAnswers = hasInProperty(pulseAnswerFilter) ? pulseAnswerFilter.in || [] : []
    if (selectedPulseAnswers.length === 0) {
      return color
    }

    const { selectedOption } = getSelectedFilterAndOptionByIndex(
      index + 1, // +1 because the 0 index is the "no data" option
      enabledFilters,
      availableFilters
    )

    if (!selectedOption) {
      return color
    }

    return selectedPulseAnswers.includes(selectedOption.value) ? color : 'grey400'
  })
}

export const EmployeePulseInsights = ({
  employeeTotalCount,
  groupBy,
}: EmployeePulseInsightsProps) => {
  const {
    hasRecords,
    processedData,
    loading,
    availableFilters,
    enabledFilters,
    onAddFilter,
    onUpdateFilter,
    dataTableFilters,
  } = useEmployeePulseData({
    groupBy,
  })

  if (loading || !processedData) {
    return <LoadingState />
  }

  const { isEmpty, showGroupBy } = processedData

  const colors = getColorsBasedOnFilters(dataTableFilters, enabledFilters, availableFilters)

  return (
    <Box height="s400" width="full">
      <Card>
        <Card.Title
          size="small"
          tooltip={i18n.t('performance_process.analytics.employee_pulse_chart.tooltip')}
        >
          {showGroupBy
            ? i18n.t('performance_process.analytics.employee_pulse_chart.title_grouped')
            : i18n.t('performance_process.analytics.employee_pulse_chart.title')}
        </Card.Title>
        <Card.Description>
          {employeeTotalCount
            ? i18n.t('performance_process.analytics.employee_pulse_chart.description_grouped', {
                employees_count: employeeTotalCount,
              })
            : ''}
        </Card.Description>
        <Card.Content>
          {isEmpty || !hasRecords ? (
            <ChartSkeleton hasRecords={hasRecords} />
          ) : (
            <PulseChart
              processedData={processedData}
              colors={colors}
              dataTableFilters={dataTableFilters}
              enabledFilters={enabledFilters}
              availableFilters={availableFilters}
              onAddFilter={onAddFilter}
              onUpdateFilter={onUpdateFilter}
              groupBy={groupBy}
              useCustomTooltip // Enable custom tooltip
            />
          )}
        </Card.Content>
      </Card>
    </Box>
  )
}
